// @flow

import React, { useState } from 'react';
import getPinRepDomain from 'app/common/react/components/PinRep/utils/getPinRepDomain';
import i18n from 'app/common/lib/i18n';
import IconButton from 'app/common/react/components/common/ExperimentalLegoGestalt/IconButton';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import Text from 'app/common/react/components/common/ExperimentalLegoGestalt/Text';
import useTrackingCallback from 'app/common/react/components/PinRep/Hooks/useTrackingCallback';
import { getCommerceAuxData } from 'app/common/lib/CommerceUtil';
import { type Pin } from 'app/common/react/components/PinRep/types';
import { useTrackingParameters } from 'app/common/react/components/PinRep/TrackingParametersContext';
import { Box, Icon } from 'gestalt';

export const styles = {
  container: {
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    borderRadius: '18px',
    height: '32px',
    maxWidth: '132px',
  },
  containerHovered: {
    backgroundColor: 'rgba(255, 255, 255, 1.0)',
  },
  containerTruncated: {
    maxWidth: '87px',
  },
  text: {
    color: '#333333',
  },
};

// Certain dense grids show a more truncated link URL to allow more space for other elements.
// Other dense grids show an icon only, with no URL displayed.
export type SourceLinkStyle = 'default' | 'truncated-text' | 'icon-only';

export type Props = {|
  pin: Pin,
  sourceLinkStyle: SourceLinkStyle,
  url: string,
|};

// SourceLink is the element that allows the user to click through directly to the pin's source
export default function SourceLink({ pin, sourceLinkStyle, url }: Props) {
  const [isHovered, setHoverState] = useState(false);

  const { id: pinId } = pin;
  const domain = getPinRepDomain(pin);
  const auxData = getCommerceAuxData(pin) || {};

  const { slotIndex } = useTrackingParameters();
  const trackClickthrough = useTrackingCallback(PCONST.thrift.EventType.PIN_CLICKTHROUGH, {
    grid_index: slotIndex,
    objectId: pinId,
    ...(getCommerceAuxData(pin) || {}),
  });
  const trackClick = useTrackingCallback(PCONST.thrift.EventType.CLICK, {
    element: PCONST.thrift.ElementType.WEBSITE_BUTTON,
    objectId: pinId,
    ...(getCommerceAuxData(pin) || {}),
  });

  // Icon-only version
  if (sourceLinkStyle === 'icon-only') {
    return (
      <NavigateLink
        auxData={auxData}
        external
        onClick={() => {
          trackClickthrough();
          trackClick();
        }}
        pinId={pinId}
        to={url}
      >
        <IconButton
          accessibilityLabel={i18n._('link', 'Website url link')}
          bgColor="white"
          icon="arrow-up-right"
          iconColor="darkGray"
          iconSize={12}
          size="sm"
        />
      </NavigateLink>
    );
  }

  return (
    <Box
      dangerouslySetInlineStyle={{
        __style: {
          ...styles.container,
          ...(isHovered ? styles.containerHovered : {}),
          ...(sourceLinkStyle === 'truncated-text' ? styles.containerTruncated : {}),
        },
      }}
      data-test-id="pinrep-source-link"
      onMouseEnter={() => setHoverState(true)}
      onMouseLeave={() => setHoverState(false)}
      position="relative"
    >
      <NavigateLink
        external
        onClick={() => {
          trackClickthrough();
          trackClick();
        }}
        pinId={pinId}
        to={url}
      >
        <Box
          alignItems="baseline"
          dangerouslySetInlineStyle={{ __style: styles.text }}
          display="flex"
          justifyContent="between"
        >
          <Box display="flex" flex="none" marginTop={3} marginBottom={3} marginStart={3}>
            <Icon
              accessibilityLabel={i18n._('link', 'Website url link')}
              color="darkGray"
              icon="arrow-up-right"
              size={9}
            />
          </Box>

          <Box
            alignItems="center"
            display="flex"
            height={18}
            marginStart={2}
            marginEnd={3}
            overflow="hidden"
          >
            <Text color="darkGray" size="sm" weight="semibold" truncate>
              {domain}
            </Text>
          </Box>
        </Box>
      </NavigateLink>
    </Box>
  );
}
